import React, { FC, useState, useEffect, Fragment } from 'react';
import { BaseModal, Button, Input, Switch } from '@cokitchen/cokitchen-components';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as WarnInfoIcon } from 'assets/icons/warning.svg';
import { SOLD_OUT_MINUTES } from 'utils/constants';
import { MealType } from 'utils/types';

type FormDataType = {
  amount: number;
  quantity: string | number;
  isInfinity: boolean;
  soldOutMinutes: number;
  active: boolean;
  targetRelatedMeals: boolean;
  targetRelatedAddons: boolean;
};
type UpdateModalProps = {
  show: boolean;
  loading: boolean;
  meal: MealType;
  onClose: () => void;
  onConfirm: (arg0: FormDataType) => void;
};

type ConfirmUpdateFooterProps = {
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const ConfirmUpdateFooter: FC<ConfirmUpdateFooterProps> = ({ onCancel, onConfirm, loading }) => (
  <div className='grid grid-cols-2 gap-x-4'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Cancel
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      loading={loading}
      onClick={onConfirm}>
      Confirm
    </Button>
  </div>
);

const WarnInfo = ({ text }: { text: string }) => {
  return (
    <div className='flex items-start rounded-[6px] py-[10px] px-[16px] bg-[#FEFAEB] mt-[1.75rem]'>
      <span className='min-w-[1rem] mr-[10px] mt-[3px]'>
        <WarnInfoIcon />
      </span>
      <span className='grow text-yellow-6 leading-[19px]'>{text}</span>
    </div>
  );
};

const SoldoutModal: FC<UpdateModalProps> = ({ show, loading, onClose, onConfirm, meal }) => {
  const [checkSoldOut, setCheckSoldOut] = useState(false);
  const [formData, setFormData] = useState({} as FormDataType);

  useEffect(() => {
    if (show) {
      const qty =
        meal?.available_no && meal?.available_no === 'INFINITE' ? '' : +meal?.available_no;
      setFormData({
        amount: +meal?.amount,
        quantity: qty,
        isInfinity: qty === '',
        soldOutMinutes: 0,
        active: !meal?.active,
        targetRelatedMeals: true,
        targetRelatedAddons: true
      });
    }
  }, [show]);

  useEffect(() => {
    if (checkSoldOut) {
      setFormData({ ...formData, soldOutMinutes: 0, quantity: 0 });
    }
  }, [checkSoldOut]);

  const sendValues = (): void => {
    onConfirm(formData);
  };

  const toggleMinute = (minutes: number) => {
    if (minutes === formData?.soldOutMinutes) setFormData({ ...formData, soldOutMinutes: 0 });
    else setFormData({ ...formData, soldOutMinutes: minutes });
  };

  return (
    <BaseModal
      isOpen={show}
      title={`Update ${meal?.name ?? 'base meals'}`}
      titleIcon={<EditIcon />}
      titleIconColor='info'
      onRequestClose={onClose}
      footer={<ConfirmUpdateFooter loading={loading} onCancel={onClose} onConfirm={sendValues} />}>
      <div className='mb-[1.5rem]'>
        <Input
          value={formData.quantity}
          label='Quantity'
          type='number'
          onChange={(e) => {
            setFormData({ ...formData, quantity: +e.target.value });
          }}
          className='mt-5'
          disabled={formData?.isInfinity || checkSoldOut}
          small
        />

        <Fragment>
          <div className='flex justify-between'>
            <Input
              checked={formData?.isInfinity}
              label='Select if infinte'
              onChange={() => setFormData({ ...formData, isInfinity: !formData.isInfinity })}
              disabled={checkSoldOut}
              className='mt-3'
              type='checkbox'
              small
            />

            <div className='flex mt-2'>
              <p className='mr-3 mt-1 text-[#858585] '>Turn off </p>
              <Switch
                active={formData.active}
                onChange={() => setFormData({ ...formData, active: !formData.active })}
              />
            </div>
          </div>

          {formData?.isInfinity && (
            <WarnInfo text='Unselect infinite option to add sold out option instead' />
          )}
          <hr className='border-[#EBEBEB] mt-6 mb-4' />
          <p className='text-gray-5'>select sold out options to add when item will be available</p>
        </Fragment>

        <Input
          checked={checkSoldOut}
          label='Sold Out?'
          onChange={() => setCheckSoldOut(!checkSoldOut)}
          disabled={formData?.isInfinity}
          className='mt-5'
          type='checkbox'
          small
        />
        {checkSoldOut && (
          <div className='grid grid-cols-3 gap-8 max-w-[420px] mt-5'>
            {SOLD_OUT_MINUTES.map((minute: number) => (
              <span
                key={minute}
                className={`border rounded-[8px] p-2 text-center ${
                  formData?.soldOutMinutes === minute
                    ? 'bg-red-1 border-red-2 text-red-5'
                    : ' border-gray-2 text-gray-5'
                }`}
                role='button'
                onClick={() => toggleMinute(minute)}>
                {minute === 60 ? '+ 1 hr' : `+ ${minute} mins`}
              </span>
            ))}
          </div>
        )}
        {checkSoldOut && <WarnInfo text='Unselect sold out to input quantity instead' />}
        <div className='flex justify-between'>
          <Input
            label='Target related meals ?'
            onChange={() =>
              setFormData({ ...formData, targetRelatedMeals: !formData.targetRelatedMeals })
            }
            checked={formData?.targetRelatedMeals}
            className='mt-5'
            type='checkbox'
            small
          />
          <Input
            checked={formData?.targetRelatedAddons}
            label='Target related addons?'
            onChange={() =>
              setFormData({ ...formData, targetRelatedAddons: !formData.targetRelatedAddons })
            }
            className='mt-5'
            type='checkbox'
            small
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default SoldoutModal;
